import React, { useEffect, useRef } from "react";

/* Cinematic dual-row marquee. Mixed serif + script typography, gold diamond glyphs,
   row 1 scrolls left, row 2 scrolls right, slow enough to read. */

const ROW_A = [
  { t: "Smile", kind: "serif", tone: "sage" },
  { t: "Dental", kind: "script", tone: "gold" },
  { t: "Brighten", kind: "serif", tone: "ink" },
  { t: "Align", kind: "script", tone: "gold" },
  { t: "Restore", kind: "serif", tone: "sage" },
  { t: "Glow", kind: "script", tone: "gold-deep" },
];

const ROW_B = [
  { t: "Whiten", kind: "script", tone: "gold" },
  { t: "Renew", kind: "serif", tone: "ink" },
  { t: "Implant", kind: "script", tone: "gold-deep" },
  { t: "Veneer", kind: "serif", tone: "sage" },
  { t: "Crown", kind: "script", tone: "gold" },
  { t: "Shine", kind: "serif", tone: "sage-deep" },
];

const toneClass = {
  sage: "text-sage-700",
  "sage-deep": "text-sage-900",
  ink: "text-ink",
  gold: "text-gold-500",
  "gold-deep": "text-gold-700",
};

function Diamond({ className = "" }) {
  return (
    <svg viewBox="0 0 24 80" className={className} aria-hidden="true" preserveAspectRatio="xMidYMid meet">
      <path d="M12 0 C 14 22, 22 36, 12 40 C 22 44, 14 58, 12 80 C 10 58, 2 44, 12 40 C 2 36, 10 22, 12 0 Z" fill="currentColor" />
    </svg>
  );
}

function Row({ items, direction = "left", duration = 50, size = "clamp(4.5rem, 12vw, 12rem)" }) {
  // Duplicate the array so the loop is seamless
  const full = [...items, ...items, ...items];
  const dir = direction === "left" ? "marqueeLeft" : "marqueeRight";

  return (
    <div className="overflow-hidden">
      <div
        className="flex items-center w-max"
        style={{
          animation: `${dir} ${duration}s linear infinite`,
          fontSize: size,
          lineHeight: 1,
        }}
      >
        {full.map((w, i) => (
          <React.Fragment key={i}>
            <span
              className={`px-8 md:px-12 whitespace-nowrap ${
                w.kind === "script"
                  ? "font-script italic"
                  : "font-display"
              } ${toneClass[w.tone] || "text-ink"}`}
              style={{
                letterSpacing: w.kind === "script" ? "-0.02em" : "-0.04em",
                fontWeight: w.kind === "script" ? 400 : 500,
              }}
            >
              {w.t}
            </span>
            <span className={`shrink-0 ${i % 2 ? "text-gold-400/70" : "text-gold-500/85"}`} style={{ width: "0.6em", height: "0.7em" }}>
              <Diamond className="w-full h-full" />
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default function Marquee() {
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const rows = e.target.querySelectorAll("[data-marquee-row]");
        rows.forEach((r) => {
          r.style.animationPlayState = e.isIntersecting ? "running" : "paused";
        });
      });
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      aria-hidden="true"
      data-testid="marquee"
      className="relative py-14 md:py-20 overflow-hidden bg-cream border-y border-sage-700/10"
    >
      {/* soft side fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-32 md:w-64 z-10 bg-gradient-to-r from-cream to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-32 md:w-64 z-10 bg-gradient-to-l from-cream to-transparent" />

      <div className="space-y-2 md:space-y-4">
        <div data-marquee-row><Row items={ROW_A} direction="left" duration={48} /></div>
        <div data-marquee-row><Row items={ROW_B} direction="right" duration={62} size="clamp(3rem, 9vw, 9rem)" /></div>
      </div>

      <style>{`
        @keyframes marqueeLeft {
          from { transform: translate3d(0,0,0); }
          to   { transform: translate3d(-33.3333%, 0, 0); }
        }
        @keyframes marqueeRight {
          from { transform: translate3d(-33.3333%, 0, 0); }
          to   { transform: translate3d(0, 0, 0); }
        }
      `}</style>
    </section>
  );
}
